import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

const glassCardStyle = {
  borderRadius: '12px',
  border: '1px solid var(--card-11-stroke, #FFF)',
  background:
    'linear-gradient(0deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.10) 100%), linear-gradient(170deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.40) 97.94%)',
  boxShadow:
    '0px 5px 20.1px 0px rgba(255, 255, 255, 0.10), 0px 15px 30px 0px rgba(0, 0, 0, 0.10), 0px 4px 25px 0px rgba(255, 255, 255, 0.35) inset',
  backdropFilter: 'blur(30px)',
};

const FALLBACK_LOGS = [
  '09:41 ENTRY Visitor',
  '09:41 ENTRY Ramesh Kumar',
  '09:41 ENTRY Ramesh Kumar',
  '09:41 ENTRY Ramesh Kumar',
  '09:41 ENTRY Ramesh Kumar',
  '09:52 ENTRY Pooja Singh',
  '10:05 EXIT Ramesh Kumar',
  '10:12 ENTRY Anil Verma',
  '10:22 ENTRY Kiran Das',
  '10:45 EXIT Visitor',
  '11:03 ENTRY Visitor',
  '11:26 EXIT Pooja Singh',
  '12:08 ENTRY Ramesh Kumar',
  '12:40 EXIT Anil Verma',
  '13:15 ENTRY Pooja Singh',
  '14:02 EXIT Kiran Das',
  '14:28 ENTRY Visitor',
  '15:05 EXIT Ramesh Kumar',
];

const CameraAttendanceLogsScreen = () => {
  const location = useLocation();
  const [personSearch, setPersonSearch] = useState('');
  const [fromTime, setFromTime] = useState('');
  const [toTime, setToTime] = useState('');

  const logs = useMemo(() => {
    const candidate = location.state?.logs;
    return Array.isArray(candidate) ? candidate : FALLBACK_LOGS;
  }, [location.state?.logs]);

  const parsedLogs = useMemo(() => {
    return logs.map((raw, index) => {
      const text = String(raw ?? '');
      const match = text.match(/^(\d{1,2}):(\d{2})\s+(.*)$/);
      if (!match) {
        return {
          id: `${text}-${index}`,
          raw: text,
          timeMinutes: null,
          personName: text,
        };
      }

      const hours = Number(match[1]);
      const minutes = Number(match[2]);
      const rest = match[3] || '';
      const timeMinutes =
        Number.isFinite(hours) && Number.isFinite(minutes) ? hours * 60 + minutes : null;

      const personName =
        rest.replace(/^(ENTRY|EXIT)\s+/i, '').trim() ||
        rest.trim() ||
        text;

      return {
        id: `${text}-${index}`,
        raw: text,
        timeMinutes,
        personName,
      };
    });
  }, [logs]);

  const filteredLogs = useMemo(() => {
    const nameQuery = personSearch.trim().toLowerCase();

    const parseTimeToMinutes = value => {
      const v = String(value || '').trim();
      if (!v) return null;
      const m = v.match(/^(\d{1,2}):(\d{2})$/);
      if (!m) return null;
      const h = Number(m[1]);
      const mm = Number(m[2]);
      if (!Number.isFinite(h) || !Number.isFinite(mm)) return null;
      return h * 60 + mm;
    };

    const fromMinutes = parseTimeToMinutes(fromTime);
    const toMinutes = parseTimeToMinutes(toTime);
    const hasTimeFilter = fromMinutes != null || toMinutes != null;

    return parsedLogs.filter(item => {
      if (nameQuery) {
        const haystack = String(item.personName || '').toLowerCase();
        if (!haystack.includes(nameQuery)) return false;
      }

      if (hasTimeFilter) {
        if (item.timeMinutes == null) return false;
        if (fromMinutes != null && item.timeMinutes < fromMinutes) return false;
        if (toMinutes != null && item.timeMinutes > toMinutes) return false;
      }

      return true;
    });
  }, [parsedLogs, personSearch, fromTime, toTime]);

  return (
    <div className="w-full h-full flex flex-col gap-3">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="min-w-[220px] flex-1">
          <label className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
            Person name
          </label>
          <input
            value={personSearch}
            onChange={e => setPersonSearch(e.target.value)}
            placeholder="Search by person name..."
            className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
          />
        </div>

        <div className="min-w-[180px]">
          <label className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
            From time
          </label>
          <input
            type="time"
            value={fromTime}
            onChange={e => setFromTime(e.target.value)}
            className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
          />
        </div>

        <div className="min-w-[180px]">
          <label className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
            To time
          </label>
          <input
            type="time"
            value={toTime}
            onChange={e => setToTime(e.target.value)}
            className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
          />
        </div>

        <div className="flex items-center justify-end md:justify-start">
          <button
            type="button"
            onClick={() => {
              setPersonSearch('');
              setFromTime('');
              setToTime('');
            }}
            disabled={!personSearch && !fromTime && !toTime}
            className="min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-white"
          >
            Clear filters
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0" style={glassCardStyle}>
        <div className="h-full rounded-lg border border-white/60 bg-white/50 p-0 overflow-hidden backdrop-blur-sm">
          {filteredLogs.length ? (
            <div className="h-full overflow-auto space-y-2">
              {filteredLogs.map(item => (
                <div
                  key={item.id}
                  className="rounded-lg bg-white/70 border border-white/60 px-3 py-2 text-sm text-neutral-800"
                >
                  {item.raw}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-neutral-700">
              {logs.length ? 'No matching logs.' : 'No logs available.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CameraAttendanceLogsScreen;
